import { useMemo } from "react";
import Fuse, { FuseResult, IFuseOptions } from "fuse.js";
import { nameofFactory } from "../helper/nameOf";
import { SearchEngineEntry } from "./SearchEngineEntry";
import { sortBy } from "../helper/orderBy";

const nameOf = nameofFactory<SearchEngineEntry>();

export interface SearchEngine {
  search: (
    searchTerm: string | undefined,
    orderByField?: keyof SearchEngineEntry,
    orderByAscOrDesc?: "asc" | "desc",
  ) => FuseResult<SearchEngineEntry>[];
  allLanguages: string[];
  allTonalities: string[];
  allTags: string[];
}

//TODO: Make bucket mandatory in the future
export function useSearchEngine(
  allEntries: readonly SearchEngineEntry[],
  limit: number,
  selectedTonalities: string[],
  selectedTags: string[],
  selectedLanguages: string[],
  bucket?: string,
): SearchEngine {
  const fuseOptions: IFuseOptions<SearchEngineEntry> = useMemo(
    () => ({
      shouldSort: true,
      findAllMatches: true,
      useExtendedSearch: true,
      keys: [nameOf("title"), "songnumbers.mnemonicNumber"],
    }),
    [],
  );

  const bucketFilteredEntries = useMemo(() => {
    return bucket ? allEntries.filter((e) => e.bucket === bucket) : allEntries;
  }, [allEntries, bucket]);

  const prefilteredEntries = useMemo(() => {
    return bucketFilteredEntries.filter((item) => {
      if (
        selectedTonalities.length > 0 &&
        (!item.tonality || !selectedTonalities.includes(item.tonality))
      ) {
        return false;
      }
      if (
        selectedTags.length > 0 &&
        !item.tags.some((tag) => selectedTags.includes(tag))
      ) {
        return false;
      }
      if (
        selectedLanguages.length > 0 &&
        (!item.language || !selectedLanguages.includes(item.language))
      ) {
        return false;
      }
      return true;
    });
  }, [
    bucketFilteredEntries,
    selectedTonalities,
    selectedTags,
    selectedLanguages,
  ]);

  // Memoize available tonalities and tags (bucket-filtered only)
  const { allLanguages, allTonalities, allTags } = useMemo(() => {
    const tonalities = new Set<string>();
    const tags = new Set<string>();
    const languages = new Set<string>();

    bucketFilteredEntries.forEach((v) => {
      if (v.tonality && v.tonality.length > 0) {
        tonalities.add(v.tonality);
      }
      v.tags.forEach((t) => tags.add(t));
      if (v.language) {
        languages.add(v.language);
      }
    });

    return {
      allLanguages: Array.from(languages),
      allTonalities: Array.from(tonalities),
      allTags: Array.from(tags),
    };
  }, [bucketFilteredEntries]);

  // Memoize the Fuse instance
  const fuse = useMemo(() => {
    return new Fuse(prefilteredEntries, fuseOptions);
  }, [prefilteredEntries, fuseOptions]);

  // Memoize the search function to ensure stable reference
  const search = useMemo(() => {
    return (
      searchTerm: string | undefined,
      orderByField: keyof SearchEngineEntry = "title",
      orderByAscOrDesc: "asc" | "desc" = "asc",
    ): FuseResult<SearchEngineEntry>[] => {
      // 1. FUSE.JS FULLTEXT SEARCH (Probabilistic)
      if (searchTerm && searchTerm.trim().length > 0) {
        const results = fuse.search(searchTerm);

        // Fuse results are already sorted by score (if shouldSort is true)
        return results.slice(0, limit);
      }

      // 2. SORT (Manual) - If fulltext search is not active
      const sortedItems = sortBy(
        prefilteredEntries,
        orderByField,
        orderByAscOrDesc,
      );

      // Map to FuseResult structure
      return sortedItems.slice(0, limit).map((item, i) => ({
        item,
        refIndex: i,
        score: 0, // Default score for non-searched items
      }));
    };
  }, [prefilteredEntries, fuse, limit]);

  return useMemo(
    () => ({
      search,
      allLanguages,
      allTonalities,
      allTags,
    }),
    [search, allLanguages, allTonalities, allTags],
  );
}
